import { forwardRef } from 'react';
import { Drawer } from '@base-ui/react/drawer';

/**
 * Edge-anchored sheet primitive, sibling to `BaseDialog`.
 *
 * `BaseDialog` is the centred modal; `BaseSheet` is the edge-anchored one. They
 * share the token set and the controlled `open` / `onClose` shape on purpose, so
 * the two read as one family.
 *
 * Two deliberate differences from `BaseDialog`, both about avoiding a second
 * owner of the same thing:
 *
 * 1. Presence and motion are owned by Base UI's own transition attributes
 *    (`data-open`, `data-starting-style`, `data-ending-style`), NOT by
 *    `useAnimatedPresence`. Base UI drives the popup's `transform` during a
 *    swipe, so a second animation owner on that same transform would be a
 *    two-writer race on one property.
 * 2. Swipe-to-dismiss is always on and is not a prop. Base UI owns the gesture
 *    and exposes no supported way to disable it on the popup (`Drawer.SwipeArea`
 *    is the invisible area that swipes the drawer OPEN, a different thing). A
 *    prop that silently failed to disable it would be worse than no prop.
 *
 * `open` is controlled by the caller. This primitive keeps no open-state of its
 * own, so the DOM can never disagree with the fact the caller owns.
 *
 * `snapPoints` (viewport fractions) hand the detents to Base UI, which keeps the
 * popup at its tallest detent and slides it down to the active one. So with snap
 * points `popupRef` lands on the frame inside the popup: the part on screen.
 */

function joinClasses(...values) {
  return values.filter(Boolean).join(' ');
}

const BaseSheetTitle = forwardRef(function BaseSheetTitle({ className = '', ...props }, ref) {
  return (
    <Drawer.Title
      ref={ref}
      className={joinClasses('base-sheet__title', className)}
      {...props}
    />
  );
});

const BaseSheetDescription = forwardRef(function BaseSheetDescription(
  { className = '', ...props },
  ref,
) {
  return (
    <Drawer.Description
      ref={ref}
      className={joinClasses('base-sheet__description', className)}
      {...props}
    />
  );
});

function BaseSheet({
  open,
  onClose,
  children,
  side = 'bottom',
  height = 'content',
  snapPoints,
  snapPoint,
  onSnapPointChange,
  modal = true,
  overlayTone = 'none',
  dismissible = true,
  initialFocus = true,
  finalFocus = true,
  popupClassName = '',
  backdropClassName = '',
  popupRef,
  ariaLabel,
  testId = 'workspace-sheet',
}) {
  const snapping = Boolean(snapPoints?.length);
  return (
    <Drawer.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && open) {
          onClose?.();
        }
      }}
      modal={modal}
      swipeDirection={side === 'bottom' ? 'down' : 'right'}
      disablePointerDismissal={!dismissible}
      snapPoints={snapPoints}
      snapPoint={snapPoint}
      onSnapPointChange={onSnapPointChange}
    >
      <Drawer.Portal>
        {overlayTone === 'none' ? null : (
          <Drawer.Backdrop
            className={joinClasses('base-sheet__backdrop', backdropClassName)}
            data-tone={overlayTone}
          />
        )}
        {/* The Viewport is NOT optional decoration: without it Base UI disables
            swipe-to-dismiss and touch scroll locking, and warns. The sheet still
            renders, which is exactly why this is easy to ship broken. */}
        <Drawer.Viewport className={`base-sheet__viewport base-sheet__viewport--${side}`}>
          <Drawer.Popup
            ref={snapping ? undefined : popupRef}
            className={joinClasses(
              'base-sheet__popup',
              `base-sheet__popup--${side}`,
              snapping ? 'base-sheet__popup--snap' : `base-sheet__popup--height-${height}`,
              popupClassName,
            )}
            style={snapping ? { '--base-sheet-snap-height': `${Math.max(...snapPoints) * 100}dvh` } : undefined}
            data-shell-sheet=""
            data-testid={testId}
            initialFocus={initialFocus}
            finalFocus={finalFocus}
            aria-label={ariaLabel}
          >
            {snapping ? <div ref={popupRef} className="base-sheet__frame">{children}</div> : children}
          </Drawer.Popup>
        </Drawer.Viewport>
      </Drawer.Portal>
    </Drawer.Root>
  );
}

BaseSheet.Title = BaseSheetTitle;
BaseSheet.Description = BaseSheetDescription;
BaseSheet.Close = Drawer.Close;
BaseSheet.Viewport = Drawer.Viewport;

export default BaseSheet;
